<script setup lang="ts">

import {SubLocation} from "@/api/dx-backend";
import GMSubLocationSelectorCard from "@/components/GameMaster/Character/GMSubLocationSelectorCard.vue";
import {ref, computed} from "vue";

interface Filter {
  name?: string;
  id?: string;
  location?: string;
  isActive?: boolean;
}

interface Props {
  subLocations: SubLocation[];
  selectedSubLocationId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  selectedSubLocationId: undefined,
});
const emit = defineEmits<{
  select: [subLocation: SubLocation];
  filterByLocation: [location: string];
  close: [];
}>();

// Filter state
const searchQuery = ref('');
const selectedLocation = ref('');
const selectedActiveFilter = ref('');

// Get unique locations from subLocations
const locations = computed(() => {
  const locs = new Set<string>();
  props.subLocations.forEach(subLoc => {
    if (subLoc.location) {
      locs.add(subLoc.location);
    }
  });
  return Array.from(locs).sort();
});

// Filtered subLocations based on search and filters
const filteredSubLocations = computed(() => {
  let filtered = props.subLocations;

  // Apply search query (name, id, description, or location)
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();
    filtered = filtered.filter(subLoc =>
      subLoc.name.toLowerCase().includes(query) ||
      subLoc.id.toLowerCase().includes(query) ||
      subLoc.description?.toLowerCase().includes(query) ||
      subLoc.location?.toLowerCase().includes(query)
    );
  }

  // Apply location filter
  if (selectedLocation.value) {
    filtered = filtered.filter(subLoc => subLoc.location === selectedLocation.value);
  }

  // Apply active filter
  if (selectedActiveFilter.value !== '') {
    const isActive = selectedActiveFilter.value === 'true';
    filtered = filtered.filter(subLoc => subLoc.is_active === isActive);
  }

  return filtered;
});

// Fast action handlers
const copySubLocationId = (subLocationId: string) => {
  navigator.clipboard.writeText(subLocationId);
};

const filterBySubLocationLocation = (subLocation: SubLocation) => {
  if (subLocation.location) {
    selectedLocation.value = subLocation.location;
    emit('filterByLocation', subLocation.location);
  }
};

// Active filters computed property
const activeFilters = computed(() => {
  const filters = [];

  if (searchQuery.value.trim()) {
    filters.push({
      type: 'search',
      label: `Search: "${searchQuery.value}"`,
      value: searchQuery.value
    });
  }

  if (selectedLocation.value) {
    filters.push({
      type: 'location',
      label: `Location: ${selectedLocation.value}`,
      value: selectedLocation.value
    });
  }

  if (selectedActiveFilter.value !== '') {
    const label = selectedActiveFilter.value === 'true' ? 'Active Only' : 'Inactive Only';
    filters.push({
      type: 'active',
      label: label,
      value: selectedActiveFilter.value
    });
  }

  return filters;
});

// Clear all filters function
const clearAllFilters = () => {
  searchQuery.value = '';
  selectedLocation.value = '';
  selectedActiveFilter.value = '';
};

// Individual filter removal functions
const removeFilter = (filterType: string) => {
  switch (filterType) {
    case 'search':
      searchQuery.value = '';
      break;
    case 'location':
      selectedLocation.value = '';
      break;
    case 'active':
      selectedActiveFilter.value = '';
      break;
  }
};

// Handle SubLocation selection
const handleSubLocationSelect = (subLocation: SubLocation) => {
  emit('select', subLocation);
};
</script>

<template>
  <div class="gm-panel gm-panel--raised sublocation-selector-container">
    <div class="header">
      <div class="header-top">
        <h2 class="gm-title title">SubLocation Selector</h2>
        <button @click="emit('close')" class="gm-btn gm-btn--ghost gm-btn--sm close-btn" title="Close SubLocation Selector">
          ×
        </button>
      </div>

      <!-- Filter Controls -->
      <div class="filters">
        <!-- Search Input -->
        <div class="filter-group">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by name, ID, description, location..."
            class="gm-input"
          />
        </div>

        <!-- Location Filter -->
        <div class="filter-group">
          <select v-model="selectedLocation" class="gm-select">
            <option value="">All Locations</option>
            <option v-for="location in locations" :key="location" :value="location">
              {{ location }}
            </option>
          </select>
        </div>

        <!-- Active Filter -->
        <div class="filter-group">
          <select v-model="selectedActiveFilter" class="gm-select">
            <option value="">All SubLocations</option>
            <option value="true">Active Only</option>
            <option value="false">Inactive Only</option>
          </select>
        </div>

        <!-- Clear All Filters Button -->
        <div class="filter-group clear-button-group">
          <button
            @click="clearAllFilters"
            class="gm-btn gm-btn--sm gm-btn--danger clear-all-btn"
            :disabled="activeFilters.length === 0"
            title="Clear all active filters"
          >
            Clear All
          </button>
        </div>
      </div>

      <!-- Active Filter Tags -->
      <div v-if="activeFilters.length > 0" class="filter-tags">
        <div class="gm-label filter-tags-label">Active Filters:</div>
        <div class="filter-tags-list">
          <div
            v-for="filter in activeFilters"
            :key="filter.type"
            class="gm-chip filter-tag"
            @click="removeFilter(filter.type)"
          >
            <span class="filter-tag-label">{{ filter.label }}</span>
            <button
              class="filter-tag-remove"
              title="Remove this filter"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="sublocations-grid-wrapper gm-scroll">
      <div class="sublocations-grid">
        <GMSubLocationSelectorCard
          v-for="subLocation in filteredSubLocations"
          :key="subLocation.id"
          :subLocation="subLocation"
          :selected="subLocation.id === props.selectedSubLocationId"
          class="sublocation-card"
          @click="handleSubLocationSelect(subLocation)"
          @copy-id="copySubLocationId"
          @filter-by-location="filterBySubLocationLocation"
        />
      </div>

      <!-- No Results Message -->
      <div v-if="filteredSubLocations.length === 0" class="gm-empty no-results">
        <p class="gm-empty__title">No SubLocations found matching your criteria.</p>
        <button @click="clearAllFilters" class="gm-btn clear-filters-btn">Clear Filters</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sublocation-selector-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 25rem;
  max-height: 37.5rem;
}

.header {
  flex-shrink: 0;
  margin-bottom: var(--gm-gap-sm);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap-sm);
  position: relative;
}

.title {
  flex: 1;
}

.close-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  padding: 0;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gm-gap-sm);
  margin-bottom: var(--gm-gap-sm);
}

.filter-group {
  flex: 1;
  min-width: 7.5rem;
}

.clear-button-group {
  flex: 0 0 auto;
  min-width: auto;
}

.filter-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--gm-gap-sm);
  margin-bottom: var(--gm-gap-sm);
}

.filter-tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gm-gap-xs);
}

.filter-tag {
  cursor: pointer;
}

.filter-tag-label {
  margin-right: 0.25rem;
}

.filter-tag-remove {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0;
  line-height: 1;
}

.sublocations-grid-wrapper {
  flex: 1;
  overflow-y: auto;
  padding-right: var(--gm-gap-sm);
}

.sublocations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  gap: var(--gm-gap);
  padding-bottom: var(--gm-gap);
  align-items: start;
}

.sublocation-card {
  cursor: pointer;
}

.no-results {
  grid-column: 1 / -1;
}
</style>
